import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { SupportTicketStatic } from "./support-ticket";
import { HttpService } from "../../services/http.service";
import { Router } from "@angular/router";
import { environment } from "../../../environments/environment";
import { StateService } from "../../services/state.service";
import { MessageService } from "primeng/api";
import { LoggerService } from "../../services/logger.service";

@Component({
  selector: "app-support-ticket",
  templateUrl: "./support-ticket.component.html",
  styleUrl: "./support-ticket.component.scss",
})
export class SupportTicketComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() header: string = "Dialog Title";
  @Input() content: string = "Dialog content.";
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  supportTktStatic: any = SupportTicketStatic;
  formData = this.supportTktStatic.FORM_DATA;
  formGroup!: FormGroup;
  impactLevel: { [key: string]: any } = {
    high: false,
    medium: false,
    low: false,
  };
  selectedFiles: File[] = [];
  browserInfo: string = "";
  osInfo: string = "";
  deviceType: string = "";
  fileUploadContent: any = {
    fileList: [],
    fileUploadPercent: 0,
    fileUploadStatus: false,
    fileUploadError: "",
  };
  latestError: any;
  showDialogLoader: boolean = false;
  maxFileCount = 10; // Maximum number of files allowed
  maxFileSize = 10 * 1024 * 1024; // 10MB in bytes

  constructor(
    private httpService: HttpService,
    private router: Router,
    private stateService: StateService,
    private messageService: MessageService,
    private loggerService: LoggerService
  ) {}

  ngOnInit(): void {
    // Initialize the form group with controls
    this.initForm();
    this.getDeviceDetails();
  }

  initForm() {
    const formControls: { [key: string]: FormControl } = {};
    this.formData.fields.forEach((field: any) => {
      formControls[field.name] = new FormControl(
        "",
        field.isRequired ? Validators.required : null
      );
    });
    this.formGroup = new FormGroup(formControls);
  }

  onFormSubmit() {
    this.showDialogLoader = true;
    this.formGroup.disable();
    const formData = new FormData();
    const expId = sessionStorage.getItem("expId");

    // Append each file under the same key `attachments`
    this.selectedFiles.length &&
      this.selectedFiles.forEach((file) => {
        formData.append("attachments", file, file.name);
      });

    // Append other form values
    formData.append("experiment_id", expId || "");
    formData.append("page_url", this.router.url);
    formData.append("browser", this.browserInfo);
    formData.append("device_type", this.deviceType);
    formData.append("operating_system", this.osInfo);
    formData.append(
      "console_log",
      JSON.stringify(this.loggerService.getErrorLogs())
    );

    // Append all form group values
    Object.keys(this.formGroup.value).forEach((key) => {
      if (key !== "attachments") {
        // Avoid overriding attachments key
        formData.append(key, this.formGroup.value[key]);
      }
    });

    const url = `${environment.userUrlV3}tickets`;

    this.httpService.postMethod(url, formData).subscribe({
      next: (res) => {
        this.showDialogLoader = false;
        this.closeDialog();
        this.showAlertMessage("info", res.message, res.ticket_id);
      },
      error: (err) => {
        this.closeDialog();
      },
    });
  }

  get is_beta_user(): boolean {
    return this.stateService.isBetaUser();
  }

  openDialog() {
    this.visible = true;
  }

  closeDialog() {
    this.visible = false;
    this.formGroup.reset();
    this.formGroup.enable();
    this.clearFileSelection();
    this.impactLevel = {
      high: false,
      medium: false,
      low: false,
    };
  }

  onClose() {
    this.closeDialog();
    this.visibleChange.emit(false);
  }

  onFileBrowse(e: any) {
    let fileList: FileList =
      e.target?.files || e.dataTransfer?.files || e.file.dataTransfer.files;

    this.selectedFiles = Array.from(fileList);

    // Check file count
    if (this.selectedFiles.length > this.maxFileCount) {
      this.fileUploadContent.fileUploadError = `You can upload up to ${this.maxFileCount} files`;
      return;
    }

    // Check file sizes
    const oversizedFiles = this.selectedFiles.filter(
      (file) => file.size > this.maxFileSize
    );
    if (oversizedFiles.length > 0) {
      this.fileUploadContent.fileUploadError = `The maximum allowed file size is ${this.maxFileSize / (1024 * 1024)} MB`;
      return;
    }

    this.selectedFiles.forEach((f: any) => {
      if (this.fileUploadContent.fileList.length < 10) {
        this.fileUploadContent.fileList.push({
          uploadedFile: f,
          fileName: String(f.name),
          filesize: this.stateService.parseFileSize(f.size),
        });
      }
    });
    this.fileUploadContent.fileUploadPercent = 100;
    this.fileUploadContent.fileUploadStatus = true;
  }

  onFileDrop(e: any) {
    this.onFileBrowse(e);
  }

  clearFileSelection() {
    this.fileUploadContent.fileList = [];
    this.selectedFiles = [];
    this.fileUploadContent.fileUploadPercent = 0;
    this.fileUploadContent.fileUploadStatus = false;
    this.fileUploadContent.fileUploadError = "";
  }

  onSelectImpactLevel(type: string) {
    // Reset color selection
    this.impactLevel = {};

    // Toggle color selection
    this.impactLevel[type] = !this.impactLevel[type];
    this.formGroup.get("impact_level")?.setValue(type);
  }

  getDeviceDetails() {
    // Get Browser details
    this.browserInfo = this.getBrowserInfo();

    // Get OS details
    this.osInfo = this.getOSInfo();

    // Determine device type
    this.deviceType = this.isMobile() ? "Mobile" : "Desktop";
  }

  getBrowserInfo(): string {
    const userAgent = navigator.userAgent;

    if (userAgent) {
      return userAgent;
    } else {
      return "Unknown Browser";
    }
  }

  getOSInfo(): string {
    const platform = navigator.platform.toLowerCase();

    if (platform.includes("win")) {
      return "Windows";
    } else if (platform.includes("mac")) {
      return "MacOS";
    } else if (platform.includes("linux")) {
      return "Linux";
    } else if (platform.includes("iphone") || platform.includes("ipad")) {
      return "iOS";
    } else if (platform.includes("android")) {
      return "Android";
    } else {
      return "Unknown OS";
    }
  }

  isMobile(): boolean {
    const userAgent = navigator.userAgent;
    return /Mobi|Android|iPhone|iPad/.test(userAgent);
  }

  showAlertMessage(type: string, heading: string, message: string) {
    this.messageService.clear();
    this.messageService.add({
      key: "s",
      severity: "success",
      summary: heading,
      detail: message,
    });
  }
}
